import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ActionBar, Panel, PageLoading, utils, Text, Hide, Modal, ResponsiveGroup, Button,
} from '@galilee/lilee';
import { useAuth } from 'state/AuthProvider';
import { useMatter } from 'state/MatterProvider';
import Container from 'components/Container';
import PROGRESS_STAGES from 'enums/ProgressStages';
import { getPageMeta } from 'utils';
import UploadSection from 'screens/components/UploadSection';
import MessageLoading from 'components/MessageLoading';
import { validateUploadedDocs } from 'actions/Upload';

const { useScrollToTop } = utils;

const UploadPage = () => {
  const { id } = useParams();
  const docId = Number(id);
  const history = useHistory();
  const { authToken } = useAuth();
  const { state } = useMatter();
  const { setScrollToTop } = useScrollToTop(true);
  const [validateDocuments, setValidateDocuments] = useState(false);
  const [isValidatingDocuments, setIsValidatingDocuments] = useState(false);
  const [showModalValidateDocuments, setShowModalValidateDocuments] = useState(false);
  const [validateDocumentsMessage, setValidateDocumentsMessage] = useState('');

  useEffect(() => {
    setScrollToTop(true);
  }, [docId, setScrollToTop]);

  if (!state.matter || !state.matterProgress || !authToken || !docId) return <PageLoading />;

  const {
    isCompletedByAnyone, isNotEditable, isNotCompletedByMeOrSubmittedOrFinalised, nextRoute, currentStep,
  } = getPageMeta(state.matter, state.matterProgress, PROGRESS_STAGES.UPLOAD, docId);

  const validateUploadedDocuments = async () => {
    if (!validateDocuments) { history.push(nextRoute); }
    setIsValidatingDocuments(true);
    try {
      const response = await validateUploadedDocs(docId, authToken);
      const message = response?.message?.trim();
      if (message) {
        setValidateDocumentsMessage(message);
        setShowModalValidateDocuments(true);
        return;
      }
      history.push(nextRoute);
    } catch (error) {
      setValidateDocumentsMessage('Unable to validate the document/s, click OK to continue.');
      setShowModalValidateDocuments(true);
    } finally { setIsValidatingDocuments(false); }
  };

  return (
    <Container>
      <Container center>
        {
        showModalValidateDocuments && (
        <Modal>
          <Modal.Body>
            <Modal.Subtitle>{validateDocumentsMessage}</Modal.Subtitle>
            <Modal.ActionContainer width="100%" justifyContent="flex-end">
              <ResponsiveGroup>
                <Button color="secondary" onClick={() => history.push(nextRoute)}>OK</Button>
              </ResponsiveGroup>
            </Modal.ActionContainer>
          </Modal.Body>
        </Modal>
        )
        }
        {isValidatingDocuments && <MessageLoading message="Please wait while we validate the document - this may take a minute." />}
        <Panel mb="0">
          <UploadSection disabled={isNotEditable} errors={currentStep.errors} setValidateDocuments={setValidateDocuments} />
        </Panel>
        <ActionBar
          showNext={isCompletedByAnyone || isNotEditable}
          onNextClick={() => validateUploadedDocuments()}
        >
          { isNotCompletedByMeOrSubmittedOrFinalised && <Text color="base60" fontSize="1"><Hide breakpoints={[0, 1]}>Upload a document or select an option to continue.</Hide></Text> }
        </ActionBar>
      </Container>
    </Container>
  );
};

export default UploadPage;
